import React, { useEffect, useRef } from "react";
import { Button, Separator } from "@/components/ui";
import { useTranslation } from "react-i18next";

interface GridPaginationProps {
  page: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  gridId?: string;
  isLoading?: boolean;
}

export const GridPagination = ({
  page,
  totalPages,
  onPageChange,
  gridId,
  isLoading = false,
}: GridPaginationProps) => {
  const { t } = useTranslation();

  const previousStateRef = useRef<{
    page: number;
    totalPages: number;
    hasLoadedBefore: boolean;
  }>({
    page: 1,
    totalPages: 1,
    hasLoadedBefore: false,
  });

  useEffect(() => {
    if (!isLoading && totalPages > 0) {
      previousStateRef.current = {
        page,
        totalPages,
        hasLoadedBefore: true,
      };
    }
  }, [isLoading, page, totalPages]);

  const handlePrevious = () => {
    if (page > 1 && onPageChange && !isLoading) onPageChange(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages && onPageChange && !isLoading) onPageChange(page + 1);
  };

  const getDisplayState = () => {
    const { hasLoadedBefore } = previousStateRef.current;

    if (isLoading && !hasLoadedBefore) {
      return null;
    }

    if (isLoading && hasLoadedBefore) {
      return {
        page: previousStateRef.current.page,
        totalPages: previousStateRef.current.totalPages,
      };
    }

    if (!isLoading && totalPages === 0) {
      return { showNoRows: true };
    }

    return { page, totalPages };
  };

  const displayState = getDisplayState();

  return (
    <>
      <Separator />
      <nav
        aria-label={t("global.pagination")}
        {...(gridId ? { "aria-controls": gridId } : {})}
        data-testid="grid-pagination"
      >
        <div className="flex items-center justify-between py-4 text-sm text-muted-foreground">
          <span aria-live="polite" aria-atomic="true">
            {displayState === null
              ? ""
              : displayState.showNoRows
                ? t("global.noRows")
                : t("global.pageOfTotal", {
                    page: displayState.page,
                    totalPages: displayState.totalPages,
                  })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={page === 1 || isLoading}
              data-testid="grid-previous-button"
              aria-label={t("global.previous", "Previous")}
              aria-disabled={page === 1 || isLoading}
            >
              {t("global.previous")}
            </Button>
            <Button
              variant="outline"
              onClick={handleNext}
              disabled={page === totalPages || isLoading}
              data-testid="grid-next-button"
              aria-label={t("global.next", "Next")}
              aria-disabled={page === totalPages || isLoading}
            >
              {t("global.next")}
            </Button>
          </div>
        </div>
      </nav>
    </>
  );
};
